import { useRef, useState } from 'react'
import { AxiosError } from 'axios'

import { ErrorBoundary } from '@/components/ErrorBoundary'
import { Popover } from '@/components/Popover'
import { Button } from '@/components/Button'
import { Input } from '@/components/Input'
import { useToast } from '@/hooks/Toast'
import { useCreateTeam } from '@/hooks/useCreateTeam'
import { useForm, ValidationRules } from '@/hooks/useForm'

import s from './InvitePopup.module.css'

type Props = {
  organizationId: string
}

const validationRules: ValidationRules = {
  teamName: {
    required: true,
    requiredMsg: '-',
  },
}

export const InvitePopup = ({ organizationId }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const createTeamMutation = useCreateTeam()
  const toast = useToast()
  const formRef = useRef<HTMLFormElement>(null)
  const { values, errors, handleChange, handleSubmit } = useForm({
    initialValues: {
      teamName: '',
    } as { teamName: string },
    validationRules: validationRules,
    formRef: formRef,
  })

  const onSubmit = async () => {
    try {
      await createTeamMutation.mutateAsync({
        name: values.teamName,
        organizationId: organizationId!,
      })
      setIsOpen(false)
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        toast.error(
          error?.response?.data?.message ?? 'Something went wrong. Try again?'
        )
      }
    }
  }

  return (
    <ErrorBoundary>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger>
          <Button variant="primary">New Team</Button>
        </Popover.Trigger>
        <Popover.Content title="New Team">
          <div className={s.content}>
            <form
              ref={formRef}
              className={s.form}
              onSubmit={handleSubmit(() => onSubmit())}
              noValidate
            >
              <Input
                name="teamName"
                placeholder="Name"
                error={errors.teamName}
                autoFocus
                fullWidth
                required
                onChange={handleChange}
              />
              <Button
                variant="primary"
                type="submit"
                disabled={createTeamMutation.isPending}
              >
                Create
              </Button>
            </form>
          </div>
        </Popover.Content>
      </Popover>
    </ErrorBoundary>
  )
}
